import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/site";
import { UploadCloud, CheckCircle2, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESPONSIBILITIES = [
  { t: "Event Planning & Coordination", d: "Collaborate with clients to understand their needs, design event concepts, and manage all aspects of execution from start to finish." },
  { t: "Sales & Client Relations", d: "Drive event sales by developing strong relationships, identifying new opportunities, and ensuring client satisfaction throughout planning." },
  { t: "Administrative Support", d: "Manage logistics including scheduling, vendor coordination, budgeting and contracts — with accurate, on-time paperwork." },
  { t: "On-Site Event Management", d: "Oversee events on the day-of: problem-solving, client & vendor interactions, and ensuring everything runs smoothly." },
  { t: "Marketing & Promotion", d: "Assist in creating promotional materials and marketing strategies to attract new clients and showcase our services." },
];

const POSITIONS = [
  "Event Coordinator",
  "Banquet / Event Server",
  "Bartender",
  "Chef / Kitchen Staff",
  "Catering Staff",
  "Setup & Décor Crew",
  "Host / Hostess",
  "Sales / Venue Manager",
  "Cleaning / Maintenance",
  "Other",
];

const EMPTY = { firstName: "", lastName: "", email: "", phone: "", position: "", experience: "" };

export default function Careers() {
  const [form, setForm] = useState(EMPTY);
  const [posSelect, setPosSelect] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPosition = (e) => {
    const val = e.target.value;
    setPosSelect(val);
    setForm((f) => ({ ...f, position: val === "Other" ? "" : val }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    if (!posSelect || (posSelect === "Other" && !form.position.trim())) {
      toast.error("Please select the position you're applying for.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append("resume", file);
      await axios.post(`${API}/careers/apply`, data);
      setDone(true);
      toast.success("Application received — thank you!");
      setForm(EMPTY);
      setPosSelect("");
      setFile(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full border-b border-navy/20 bg-transparent py-3 font-light text-navy placeholder:text-navy/40 focus:border-gold focus:outline-none transition-colors";

  return (
    <div data-testid="careers-page">
      <PageHeader
        overline="Join Our Team"
        titleLines={["Build your", <span key="i" className="italic text-gold">career with us</span>]}
        subtitle="From event coordinators and chefs to servers, bartenders and décor crew — we're always looking for talented people who love creating unforgettable weddings and celebrations. Tell us the role you're after and attach your resume below."
        image={IMAGES.floralArch}
        imageAlt="Vista Banquet Hall floral arrangement"
        testId="careers-header"
      />

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16 items-start">
          {/* responsibilities */}
          <div className="lg:col-span-6">
            <Reveal>
              <span className="overline text-gold">Key Responsibilities</span>
              <h2 className="mt-5 font-serif text-3xl md:text-4xl font-light tracking-tight text-navy">
                What you'll do at Vista.
              </h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {RESPONSIBILITIES.map((r, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex items-start gap-4 border-b border-navy/10 pb-6">
                    <Briefcase size={18} className="mt-1 text-gold shrink-0" />
                    <div>
                      <h3 className="font-serif text-2xl font-light text-navy">{r.t}</h3>
                      <p className="mt-1 text-navy/60 font-light leading-relaxed">{r.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* application form */}
          <div className="lg:col-span-6">
            <Reveal>
              <div className="relative bg-white border border-gold/40 shadow-[0_30px_80px_-40px_rgba(11,19,43,0.35)] rounded-sm p-6 md:p-10">
                {done ? (
                  <motion.div
                    data-testid="careers-success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center py-16 text-center"
                  >
                    <CheckCircle2 size={48} className="text-gold" />
                    <h3 className="mt-6 font-serif text-3xl font-light text-navy">Application Received</h3>
                    <p className="mt-3 max-w-sm text-navy/60 font-light">
                      Thank you for your interest in joining Vista Banquet Hall. Our team will be in touch soon.
                    </p>
                    <button data-testid="careers-reset" onClick={() => setDone(false)} className="btn-outline text-navy hover:bg-navy hover:text-ivory mt-8">
                      Submit Another
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={onSubmit} data-testid="careers-form" className="space-y-7">
                    <div className="text-center">
                      <span className="overline text-gold">Apply Now</span>
                      <h3 className="mt-3 font-serif text-3xl font-light text-navy">Careers Application</h3>
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <input data-testid="careers-firstname" value={form.firstName} onChange={update("firstName")} placeholder="First Name" className={field} />
                      <input data-testid="careers-lastname" value={form.lastName} onChange={update("lastName")} placeholder="Last Name" className={field} />
                    </div>
                    <input data-testid="careers-email" type="email" value={form.email} onChange={update("email")} placeholder="Email" className={field} />
                    <input data-testid="careers-phone" value={form.phone} onChange={update("phone")} placeholder="Phone" className={field} />

                    <div>
                      <select data-testid="careers-position" value={posSelect} onChange={onPosition} className={`${field} ${posSelect ? "text-navy" : "text-navy/40"}`}>
                        <option value="" disabled>Position you're applying for</option>
                        {POSITIONS.map((p) => (
                          <option key={p} value={p} className="text-navy">{p}</option>
                        ))}
                      </select>
                      {posSelect === "Other" && (
                        <input
                          data-testid="careers-position-other"
                          value={form.position}
                          onChange={update("position")}
                          placeholder="Please specify the position"
                          className={`${field} mt-4`}
                        />
                      )}
                    </div>

                    <textarea data-testid="careers-experience" value={form.experience} onChange={update("experience")} rows={3} placeholder="Do you have experience? Tell us about it." className={`${field} resize-none`} />

                    <div>
                      <input ref={fileRef} data-testid="careers-resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files[0] || null)} className="hidden" />
                      <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full items-center justify-center gap-3 rounded-sm border border-dashed border-gold/60 bg-blush/40 py-6 text-navy/70 font-light transition-colors hover:bg-blush">
                        <UploadCloud size={20} className="text-gold" />
                        {file ? file.name : "Upload Resume (PDF, DOC)"}
                      </button>
                    </div>

                    <button type="submit" data-testid="careers-submit" disabled={submitting} className="btn-gold w-full disabled:opacity-60">
                      {submitting ? "Submitting…" : "Submit Application"}
                    </button>
                    <p className="text-center text-xs text-navy/40">
                      Applications are sent directly to our events team.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
